import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { FiChevronDown, FiFilter, FiX } from "react-icons/fi";
import { CompactTextInput } from "newui/components/ui/InputWithLabel/CompactTextInput";

export interface TextSearchFilterProps {
  debounceMs?: number;
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}

export const TextSearchFilter = ({
  label,
  value,
  onChange,
  placeholder = "Search...",
  debounceMs = 300,
}: TextSearchFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onChange(newValue);
    }, debounceMs);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalValue("");
    onChange("");
  };

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const isActive = value.length > 0;

  return (
    <Menu isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <MenuButton
        as={Button}
        rightIcon={<FiChevronDown />}
        leftIcon={<FiFilter size={14} />}
        variant="ghost"
        size="xs"
        onClick={handleOpen}
        _hover={{ bg: "gray.100" }}
        _active={{ bg: "gray.200" }}
        data-testid={`text-search-filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Text fontSize="xs">{label}</Text>
          {isActive && (
            <Badge colorScheme="blue" borderRadius="full" fontSize="2xs" px={2}>
              1
            </Badge>
          )}
        </Box>
      </MenuButton>
      <MenuList p={2} minW="220px">
        <Box display="flex" alignItems="center" gap={1}>
          <CompactTextInput
            ref={inputRef}
            value={localValue}
            onChange={handleChange}
            placeholder={placeholder}
            data-testid={`text-search-filter-input-${label.toLowerCase().replace(/\s+/g, "-")}`}
          />
          {localValue.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleClear}
              aria-label="Clear filter"
              data-testid={`text-search-filter-clear-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <FiX />
            </Button>
          )}
        </Box>
      </MenuList>
    </Menu>
  );
};
